import { MentionedUsersFieldFragment } from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames/bind';
import { features } from 'constants/featureFlags';
import { TFunction } from 'next-i18next';
import { PostBody } from 'types/post';

import React from 'react';

import { Button } from '@thinkific/toga-react';

import AttachmentDisplayer from 'components/AttachmentDisplayer';
import { useCommunityContext } from 'components/CommunityContext';
import HighlightContainer from 'components/HighlightArea/HighlightContainer';
import Content from 'components/new/Posts/ReadOnlyContent/Content';
import { useGlobalState } from 'components/new/providers/GlobalStateProvider';

import { Attachment } from 'schema/Post/types';

import styles from '../Post.module.scss';

const cx = classNames.bind(styles);

interface ExpandableContentProps {
  content: string;
  body: PostBody;
  hasAttachment: boolean;
  attachments?: Attachment[];
  mentionedUsers?: MentionedUsersFieldFragment | null;
  isReplyPanel?: boolean;
  t: TFunction;
  isComment?: boolean;
  id: string;
}

const ExpandableContent = ({
  content,
  body,
  hasAttachment,
  attachments,
  mentionedUsers,
  isReplyPanel,
  t,
  isComment,
  id,
}: ExpandableContentProps) => {
  const { isContentExpanded, expandCollapseContent } = useGlobalState();
  const { featureFlags } = useCommunityContext();
  const tiptapEditorEnabled = featureFlags[features.TIPTAP_EDITOR];
  const handleExpandCollapse = () => {
    expandCollapseContent(id);
  };

  const showExpandedView = isContentExpanded(id);

  const readContentButton = content.length > 480 && (
    <Button
      appearance="accessible-link"
      className={cx('post__content--button')}
      data-qa="read-post__button"
      type="button"
      onClick={handleExpandCollapse}
    >
      {showExpandedView ? t('Read less') : t('Read more')}
    </Button>
  );

  return (
    <article
      className={cx('post__content', hasAttachment ? 'post__content--attachment' : '')}
      data-qa="post__content"
    >
      {tiptapEditorEnabled ? (
        <Content body={body} id={id} />
      ) : (
        <>
          <div
            className={cx(
              'post__content__text',
              showExpandedView ? '' : 'post__content__text--clamped'
            )}
          >
            <HighlightContainer content={content} mentionedUsers={mentionedUsers} />
          </div>
          {readContentButton}
        </>
      )}
      {hasAttachment && (
        <AttachmentDisplayer
          attachments={attachments || []}
          isComment={isComment}
          isReplyPanel={isReplyPanel}
          t={t}
        />
      )}
    </article>
  );
};

export default ExpandableContent;
